import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isAmbientNotificationType } from "@/lib/notificationCategories";
import { Mascot } from "@/components/Mascot";
import { cn } from "@/lib/utils";

// How long the heads-up banner stays on screen before auto-dismissing.
const VISIBLE_MS = 6000;
// Matches the CSS transition duration below so we unmount after sliding out.
const SLIDE_MS = 300;

interface BannerData {
  id: string;
  title: string;
  message: string;
  /** Deep-link target, derived from the notification payload. */
  path: string | null;
}

interface NotificationRow {
  id: string;
  type: string;
  title: string;
  message: string;
  data: Record<string, unknown> | null;
}

/** Build the in-app deep-link for an ambient notification row. */
function derivePath(data: Record<string, unknown> | null): string | null {
  if (!data) return null;
  const notifPath = data.notif_path;
  if (typeof notifPath === "string" && notifPath.startsWith("/")) return notifPath;
  const slug = data.cafe_slug;
  if (typeof slug === "string" && slug.length > 0) return `/cafe/${slug}`;
  return null;
}

/**
 * Transient, top-of-screen "heads-up" banner for ambient notifications
 * (currently nearby check-ins). These are intentionally kept OUT of the bell —
 * they slide down when the event arrives while the app is open, auto-dismiss,
 * and deep-link to the café on tap. This mirrors the in-app activity banner
 * pattern used by Instagram, X, Strava, etc.
 */
export function ActivityBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [shown, setShown] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (hideTimer.current) clearTimeout(hideTimer.current);
    if (unmountTimer.current) clearTimeout(unmountTimer.current);
  };

  const dismiss = () => {
    clearTimers();
    setShown(false);
    unmountTimer.current = setTimeout(() => setBanner(null), SLIDE_MS);
  };

  const show = (next: BannerData) => {
    clearTimers();
    setBanner(next);
    // Mount first, then flip `shown` on the next frame so the slide-in animates.
    requestAnimationFrame(() => setShown(true));
    hideTimer.current = setTimeout(dismiss, VISIBLE_MS);
  };

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`activity-banner-${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_notifications",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as NotificationRow;
          if (!isAmbientNotificationType(row.type)) return;
          show({
            id: row.id,
            title: row.title,
            message: row.message,
            path: derivePath(row.data),
          });
        },
      )
      .subscribe();

    return () => {
      clearTimers();
      supabase.removeChannel(channel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!banner) return null;

  const handleClick = () => {
    if (banner.path) navigate(banner.path);
    dismiss();
  };

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center px-3 pt-[calc(env(safe-area-inset-top)+0.5rem)]"
      role="status"
      aria-live="polite"
    >
      <div
        className={cn(
          "pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border border-white/10 bg-espresso p-3 shadow-lift transition-all duration-300 ease-out",
          shown ? "translate-y-0 opacity-100" : "-translate-y-6 opacity-0",
        )}
      >
        <button
          type="button"
          onClick={handleClick}
          className="flex min-w-0 flex-1 items-center gap-3 text-left"
        >
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-cream/10">
            <Mascot mood="happy" size="sm" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm font-semibold text-cream">
              {banner.title}
            </span>
            <span className="block truncate text-xs text-cream/70">
              {banner.message}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-cream/60 transition-colors hover:bg-cream/10 hover:text-cream"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
